import { useEffect, ReactNode } from "react"
import { format, parseISO } from "date-fns"
import { Clock, MapPin, Truck, UserRound, X } from "lucide-react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import {
  formatPalletExchangeValue,
  formatTemperatureRange,
} from "@/lib/orderCargoFormat"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Profile } from "@/reducers/profilesSlice"
import {
  firstPickupStop,
  lastDeliveryStop,
  PlannedOrder,
} from "@/reducers/plannedOrdersSlice"
import { RouteStop } from "@/reducers/routes2gSlice"
import { geocodePlannedOrderStops } from "./geocodePlannedOrder"
import { plannedOrderFlags } from "./plannerFlags"
import i18n from "@/lib/i18n/i18n"

type PlannedOrderDetailPanelProps = {
  order: PlannedOrder
  onClose: () => void
  dispatchers: Profile[]
  assignedCountByDispatcher: Record<string, number>
  // Label of the vehicle the order is assigned to (plates • driver)
  assignedVehicleLabel?: string
  onAssignToDispatcher: (orderId: string, dispatcherJid: string) => void
  // Switches to the on-timeline vehicle-assign mode
  onPickVehicle: (orderId: string) => void
  onUnassign: (orderId: string) => void
  // "Cofnij do puli" is Planner-only — a regular dispatcher never sees it.
  isPlannerRoleEnabled: boolean
  // Called when stops get enriched (e.g. lazily geocoded coordinates)
  onOrderUpdate?: (order: PlannedOrder) => void
}

const formatStopTime = (t: TFunction, value?: string) => {
  if (!value) return t("planner.common.noDate")
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) return value
  return format(parsed, "dd.MM HH:mm")
}

export const plannedOrderRouteLabel = (order: PlannedOrder): string => {
  const pickup = firstPickupStop(order)
  const delivery = lastDeliveryStop(order)
  const stopLabel = (stop: typeof pickup) =>
    stop?.location.locality ||
    stop?.location.formattedAddress ||
    i18n.t("planner.orderDetail.noAddress")
  return `${stopLabel(pickup)} → ${stopLabel(delivery)}`
}

const stopAddressLabel = (stop?: RouteStop) =>
  stop?.location.formattedAddress || stop?.location.locality

// Coordinates when we have them (more accurate directions than a text
// address), otherwise fall back to whatever address text is available.
const stopMapPoint = (stop?: RouteStop): string | undefined => {
  if (!stop) return undefined
  if (stop.location.lat && stop.location.lon) {
    return `${stop.location.lat},${stop.location.lon}`
  }
  return stopAddressLabel(stop)
}

const assignmentStatusLabel = (t: TFunction, order: PlannedOrder): string => {
  if (order.assignmentStatus === "unassigned")
    return t("planner.orderDetail.status.unassigned")
  if (order.assignmentStatus === "dispatcher")
    return t("planner.orderDetail.status.dispatcher")
  return t("planner.orderDetail.status.vehicle")
}

const detailRow = (label: string, value: ReactNode) => (
  <div className="flex items-start justify-between gap-4">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right font-medium text-foreground">{value}</span>
  </div>
)

// Inline detail panel — replaces the right sidebar's two lists while an
// order is selected (mirrors the andrzej/planing-tool mockup's task detail
// panel). The vehicle-assign flow itself stays on the timeline (assign mode
// in PlannerTimelineView); this panel only triggers it via onPickVehicle.
const PlannedOrderDetailPanel = ({
  order,
  onClose,
  dispatchers,
  assignedCountByDispatcher,
  assignedVehicleLabel,
  onAssignToDispatcher,
  onPickVehicle,
  onUnassign,
  isPlannerRoleEnabled,
  onOrderUpdate,
}: PlannedOrderDetailPanelProps) => {
  const { t } = useTranslation()
  const orderId = order.id

  // Manually entered stops carry no coordinates — resolve them lazily so the
  // distance ranking (and the map link below) works, and persist the
  // enrichment (merged into current state by the thunk, never clobbering
  // assignment).
  useEffect(() => {
    if (!onOrderUpdate) return
    let cancelled = false
    void geocodePlannedOrderStops(order).then((enriched) => {
      if (enriched && !cancelled) {
        onOrderUpdate(enriched)
      }
    })
    return () => {
      cancelled = true
    }
    // Re-run only per order identity — the enriched order flows back via props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  const pickup = firstPickupStop(order)
  const delivery = lastDeliveryStop(order)
  const flags = plannedOrderFlags(order)
  const isVehicleAssigned = order.assignmentStatus === "vehicle"
  const assignedDispatcherName = order.assignedDispatcherJid
    ? dispatchers.find(
        (dispatcher) => dispatcher.jid === order.assignedDispatcherJid,
      )?.name || order.assignedDispatcherJid
    : undefined

  const originPoint = stopMapPoint(pickup)
  const destinationPoint = stopMapPoint(delivery)
  const mapUrl =
    originPoint && destinationPoint
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
          originPoint,
        )}&destination=${encodeURIComponent(destinationPoint)}`
      : undefined

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--panel-color)]">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
        <p className="flex min-w-0 items-center gap-1.5 truncate text-sm font-semibold">
          {flags.from && <span aria-hidden>{flags.from} </span>}
          {order.metadata?.ref_number || t("planner.common.plannedOrder")}
          {flags.to && <span aria-hidden> {flags.to}</span>}
        </p>
        <button
          type="button"
          aria-label={t("planner.orderDetail.closeDetails")}
          onClick={onClose}
          className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        {/* Route card */}
        <div className="rounded-lg border bg-background p-3 shadow-sm">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate">
              {stopAddressLabel(pickup) ||
                t("planner.orderDetail.noPickupAddress")}
            </span>
          </div>
          <div className="my-1 ml-[7px] h-3 border-l border-dashed border-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate">
              {stopAddressLabel(delivery) ||
                t("planner.orderDetail.noDeliveryAddress")}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t("planner.orderDetail.pickupAt", {
                time: formatStopTime(t, pickup?.scheduled_arrival_time),
              })}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t("planner.orderDetail.deliveryAt", {
                time: formatStopTime(t, delivery?.scheduled_arrival_time),
              })}
            </span>
          </div>
          {mapUrl && (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2 w-full"
              asChild
            >
              <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                <MapPin className="mr-1.5 h-3.5 w-3.5" />
                {t("planner.orderDetail.showOnMap")}
              </a>
            </Button>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-col gap-2 border-t pt-3 text-sm">
          {detailRow(
            t("planner.orderDetail.statusLabel"),
            <Badge variant="outline">{assignmentStatusLabel(t, order)}</Badge>,
          )}
          {order.metadata?.customer_name &&
            detailRow(
              t("planner.orderDetail.company"),
              order.metadata.customer_name,
            )}
          {detailRow(
            t("planner.orderDetail.dispatcher"),
            assignedDispatcherName ? (
              <span className="flex items-center justify-end gap-1.5">
                <UserRound className="h-3.5 w-3.5 shrink-0" />
                {assignedDispatcherName}
              </span>
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {t("planner.orderDetail.assignDispatcher")}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="max-h-80 w-64 overflow-y-auto p-1.5"
                >
                  {dispatchers.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">
                      {t("planner.orderDetail.noDispatchers")}
                    </p>
                  ) : (
                    dispatchers.map((dispatcher) => (
                      <Button
                        key={dispatcher.jid}
                        variant="ghost"
                        className="w-full min-w-0 justify-between gap-2"
                        onClick={() =>
                          onAssignToDispatcher(order.id, dispatcher.jid)
                        }
                      >
                        <span className="min-w-0 truncate">
                          {dispatcher.name || dispatcher.jid}
                        </span>
                        <Badge variant="secondary" className="shrink-0">
                          {assignedCountByDispatcher[dispatcher.jid] ?? 0}
                        </Badge>
                      </Button>
                    ))
                  )}
                </PopoverContent>
              </Popover>
            ),
          )}
          {isVehicleAssigned &&
            detailRow(
              t("planner.orderDetail.vehicle"),
              <span className="flex items-center justify-end gap-1.5">
                <Truck className="h-3.5 w-3.5 shrink-0" />
                {assignedVehicleLabel || order.assignedVehicleChatJid}
              </span>,
            )}
          {detailRow(
            t("planner.orderDetail.orderNumber"),
            order.metadata?.ref_number || order.id,
          )}
          {order.metadata?.weight &&
            detailRow(t("planner.orderDetail.weight"), order.metadata.weight)}
          {formatPalletExchangeValue(order.metadata?.pallet_exchange) &&
            detailRow(
              t("planner.orderDetail.palletExchange"),
              formatPalletExchangeValue(order.metadata?.pallet_exchange),
            )}
          {formatTemperatureRange(
            order.metadata?.temperature_min,
            order.metadata?.temperature_max,
          ) &&
            detailRow(
              t("planner.orderDetail.temperature"),
              formatTemperatureRange(
                order.metadata?.temperature_min,
                order.metadata?.temperature_max,
              ),
            )}
          {order.metadata?.price_netto_eur !== undefined &&
            detailRow(
              t("planner.orderDetail.price"),
              `${order.metadata.price_netto_eur} EUR`,
            )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
          {isPlannerRoleEnabled && order.assignmentStatus !== "unassigned" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnassign(order.id)}
            >
              {t("planner.orderDetail.returnToPool")}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPickVehicle(order.id)}
          >
            <Truck className="mr-1.5 h-3.5 w-3.5" />
            {isVehicleAssigned
              ? t("planner.orderDetail.changeVehicle")
              : t("planner.orderDetail.assignToVehicle")}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PlannedOrderDetailPanel
